import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import styles from '../../styles/components/Features.module.css';

const How = () => {
  const [activeSection, setActiveSection] = useState('fulfillment');
  const [activeFulfillmentStep, setActiveFulfillmentStep] = useState(0);
  const [activePaymentsStep, setActivePaymentsStep] = useState(0);
  const stepRefs = useRef({ fulfillment: {}, payments: {} });
  const containerRefs = useRef({ fulfillment: null, payments: null });
  const observersRef = useRef({ fulfillment: null, payments: null });

  const steps = {
    fulfillment: [
      {
        text: "Connect your online store seamlessly and synchronize products with our platform.",
        image: "/Images/ord/step1.avif"
      },
      {
        text: "AI-powered inventory forecasting helps you choose the right products.",
        image: "/Images/ord/step2.avif"
      },
      {
        text: "Store products in local warehouses for faster and cost-effective shipping.",
        image: "/Images/ord/step3.avif"
      },
      {
        text: "Orders are picked, packed, and shipped directly to customers, ensuring timely and reliable delivery.",
        image: "/Images/ord/step4.png"
      }
    ],
    payments: [
      {
        text: "Complete KYB verification and receive USD account details for secure global transactions.",
        image: "/Images/globpay/step1.avif"
      },
      {
        text: "Link your online stores and marketplaces for streamlined payment processing with built-in fraud checks.",
        image: "/Images/globpay/step2.avif"
      },
      {
        text: "Process payments across multiple currencies with real-time monitoring via dashboard.",
        image: "/Images/globpay/step3.avif"
      },
      {
        text: "Access funds instantly through platform debit card or withdraw to local bank accounts.",
        image: "/Images/globpay/step4.png"
      }
    ],
  };

  const setupObserver = (section) => {
    if (window.innerWidth >= 768 && containerRefs.current[section]) {
      if (observersRef.current[section]) {
        observersRef.current[section].disconnect();
      }
  
      observersRef.current[section] = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) { 
              const step = parseInt(entry.target.getAttribute('data-step'));
              if (section === 'fulfillment') {
                setActiveFulfillmentStep(step);
              } else {
                setActivePaymentsStep(step);
              }
            }
          });
        },
        {
          root: containerRefs.current[section],
          threshold: 0.5,
          rootMargin: '0px',
        }
      );
  
      Object.values(stepRefs.current[section]).forEach((ref) => {
        if (ref) {
          observersRef.current[section].observe(ref);
        }
      });
    }
  };
  
  const handleSectionChange = (section) => {
    if (section !== activeSection) {
      setActiveSection(section);
      if (section === 'fulfillment') {
        setActiveFulfillmentStep(window.innerWidth < 768 ? steps.fulfillment.length - 1 : 0);
      } else {
        setActivePaymentsStep(window.innerWidth < 768 ? steps.payments.length - 1 : 0);
      }
      setTimeout(() => setupObserver(section), 100);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setActiveFulfillmentStep(steps.fulfillment.length - 1);
        setActivePaymentsStep(steps.payments.length - 1);
        Object.values(observersRef.current).forEach(observer => observer?.disconnect());
      } else {
        setupObserver('fulfillment');
        setupObserver('payments');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      Object.values(observersRef.current).forEach(observer => observer?.disconnect());
    };
  }, []);

  useEffect(() => {
    if (window.innerWidth >= 768) {
      setupObserver('fulfillment');
      setupObserver('payments');
    }
  }, []);

  return (
    <section className="py-12 bg-blue-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h3 className="text-base font-semibold leading-7 text-blue-600">Process</h3>
          <motion.h2
            className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            How We Do It
          </motion.h2>
          <p className="mt-4 text-xl leading-8 text-gray-600">
            Simple steps to start selling in MENAP
          </p>
        </div>

        <div className="flex justify-center space-x-4 mb-12 p-3 rounded-2xl max-w-2xl mx-auto">
          <button
            onClick={() => handleSectionChange('fulfillment')}
            className={`px-6 py-3 text-lg font-medium rounded-xl transition-all ${
              activeSection === 'fulfillment'
                ? 'bg-blue-600 text-white ring-1 ring-blue-500/20'
                : 'bg-white text-gray-700 hover:bg-blue-50 ring-1 ring-blue-200'
            }`}
          >
            Order Fulfillment
          </button>
          <button
            onClick={() => handleSectionChange('payments')}
            className={`px-6 py-3 text-lg font-medium rounded-xl transition-all ${
              activeSection === 'payments'
                ? 'bg-blue-600 text-white ring-1 ring-blue-500/20'
                : 'bg-white text-gray-700 hover:bg-blue-50 ring-1 ring-blue-200'
            }`}
          >
            Global Payments
          </button>
        </div>

        <div className="max-w-6xl mx-auto bg-white p-8 rounded-2xl ring-1 ring-gray-200/50 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]">
          <h3 className="text-2xl font-semibold mb-8 text-gray-900">
            {activeSection === 'fulfillment' ? 'Order Fulfillment' : 'Global Payments'}
          </h3>
          
          <div className="relative">
            <div className="relative md:h-[300px]"> 
              <div className="grid md:grid-cols-4 grid-cols-1 gap-12 md:gap-4">
                {steps[activeSection].map((step, index) => (
                  <div
                    key={index}
                    className={`transition-opacity duration-500 ${
                      window.innerWidth >= 768 ? 
                        ((activeSection === 'fulfillment' && index <= activeFulfillmentStep) ||
                        (activeSection === 'payments' && index <= activePaymentsStep)
                          ? 'opacity-100'
                          : 'opacity-0 pointer-events-none')
                        : 'opacity-100'
                    }`}
                  >
                    <div className="flex flex-col md:items-center px-4 relative">
                      <div className="md:hidden text-center mb-6">
                        <span className={`inline-flex items-center justify-center w-8 h-8 text-white rounded-full mb-4 mx-auto bg-blue-600`}>
                          {index + 1}
                        </span>
                        <p className="text-gray-600 max-w-md mx-auto">{step.text}</p>
                      </div>
                      
                      <div className="w-full">
                        <img
                          src={step.image}
                          alt={`Step ${index + 1}`}
                          className="w-full max-w-[300px] h-auto object-cover rounded-lg shadow-sm mx-auto" 
                        />
                      </div>
                      
                      <div className="hidden md:block text-center mt-8"> {/* Reduced margin from 8 to 6 */}
                        <span className={`inline-flex items-center justify-center w-8 h-8 text-white rounded-full mb-4 mx-auto bg-blue-600`}>
                          {index + 1}
                        </span>
                        <p className="text-gray-600 max-w-md mx-auto">{step.text}</p>
                      </div>
                    </div>
                  </div>                
                ))}
              </div>
            </div>

            <div 
              style={{ position: '-webkit-sticky', height: '100px', width: '100%' }} 
            >
              {/* Scroll containers */}
              <div 
                ref={(el) => (containerRefs.current.fulfillment = el)}
                className={`absolute inset-0 overflow-y-auto scroll-snap-y-mandatory scroll-align-start ${styles['scrollbar-hide']} ${
                  activeSection !== 'fulfillment' ? 'pointer-events-none' : ''
                }`}
              >
                <div className="space-y-[150px]">
                  {steps.fulfillment.map((_, index) => (
                    <div
                      key={index}
                      ref={(el) => (stepRefs.current.fulfillment[index] = el)}
                      data-step={index}
                      className="h-[100px]" 
                    />
                  ))}
                </div>
              </div>

              <div 
                ref={(el) => (containerRefs.current.payments = el)}
                className={`absolute inset-0 overflow-y-auto scroll-snap-y-mandatory scroll-align-start ${styles['scrollbar-hide']} ${
                  activeSection !== 'payments' ? 'pointer-events-none' : ''
                }`}
              >
                <div className="space-y-[150px]"> 
                  {steps.payments.map((_, index) => (
                    <div
                      key={index}
                      ref={(el) => (stepRefs.current.payments[index] = el)}
                      data-step={index}
                      className="h-[100px]" 
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default How;
